import {
  compact,
  every,
  filter,
  forEach,
  get,
  isEmpty,
  keys,
  last,
  map,
  orderBy,
  partition,
  range,
  reduce,
  sortBy,
  sumBy,
  toLower,
  uniq,
  values
} from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { getColorFromId } from "../../components/labels/utils";
import { isActiveInstall } from "../../pages/Workspace/InstallsData/utils";
import { Entity } from "../../utils/constants";
import {
  classificationBoundaries,
  getClassification,
  relativePerformanceClassifications
} from "../../utils/hotspotUtils";
import {
  Contact,
  EtlHotspot,
  Hotspot,
  HotspotLocation,
  HotspotMaker,
  Install
} from "../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../utils/useFilteredLabels";
import { useHntMarket } from "../../utils/useHntMarket";
import useMapConfig, {
  relativePerformanceMetrics
} from "../../utils/useMapConfig";
import { useAuthentication } from "../authentication";
import { useContacts } from "../contacts";
import { useDiscordChannels } from "../discordChannels";
import { useGoogleAnalytics } from "../googleAnalytics";
import { useHotspotMakers } from "../hotspotMakers";
import {
  HotspotMakerAddressKey,
  hotspotMakerMetadata,
  unlistedMakers
} from "../hotspotMakers/HotspotMakersProvider";
import { useHotspots } from "../hotspots";
import { useInstalls } from "../installs";
import { useLabels } from "../labels";
import { useLocations } from "../locations";
import { useTransmitScales } from "../transmitScales";
import { HexTransmitScaleInfo } from "../transmitScales/TransmitScalesProvider";
import { useWallets } from "../wallets";

type ContextValue = {
  filteredList: {
    [Entity.hotspot]: Hotspot[];
    [Entity.hex]: HexTransmitScaleInfo[];
    [Entity.location]: HotspotLocation[];
    [Entity.contact]: Contact[];
    [Entity.install]: Install[];
    [Entity.hotspotMaker]: HotspotMaker[];
  };
  getClassificationValue: (hotspot: Hotspot, key: string) => number;
  renderClassificationValue: (value: number, key: string) => string;
  histogramDataMap: {
    [iteratee: string]: Array<{ name: string; count: number }>;
  };
  histogramBucketIndexMap: {
    [hotspotId: string]: { [iteratee: string]: number };
  };
  showRelativePerformance: boolean;
  toggleShowRelativePerformance: (
    entity: Entity,
    metricIteratee: string
  ) => void;
  smartLabelFilters: {
    [Entity.hotspot]: { [key: string]: (item: Hotspot) => boolean };
    [Entity.hotspotMaker]: { [key: string]: (item: HotspotMaker) => boolean };
    [Entity.hex]: { [key: string]: (item: HexTransmitScaleInfo) => boolean };
    [Entity.location]: { [key: string]: (item: HotspotLocation) => boolean };
    [Entity.contact]: { [key: string]: (item: Contact) => boolean };
    [Entity.install]: { [key: string]: (item: Install) => boolean };
  };
};

const FilteredListContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const FilteredListProvider: React.FC<Props> = (props) => {
  const { user } = useAuthentication();
  const { trackEvent } = useGoogleAnalytics();
  const { locationsAroundCenter } = useLocations();
  const {
    hotspotsAroundCenter,
    hotspotRewardsSummary,
    hotspotWitnessesCountsMap,
    walletVerificationStatusMap
  } = useHotspots();
  const { hotspotMakersById } = useHotspotMakers();
  const { contacts } = useContacts();
  const { installs } = useInstalls();
  const { wallets } = useWallets();
  const { channels } = useDiscordChannels();
  const {
    calculatedTransmitScales,
    problematicHexes,
    getHexTransmitScaleInfo,
    isTransmitScaleActive,
    isHip17Experiment
  } = useTransmitScales();
  const { displayInCurrency } = useHntMarket();
  const { labels, linkedLabels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [filteredList, setFilteredList] = useState<{
    [Entity.hotspot]: EtlHotspot[];
    [Entity.hex]: HexTransmitScaleInfo[];
    [Entity.location]: HotspotLocation[];
    [Entity.contact]: Contact[];
    [Entity.install]: Install[];
    [Entity.hotspotMaker]: HotspotMaker[];
  }>({
    [Entity.hotspot]: [],
    [Entity.hex]: [],
    [Entity.location]: [],
    [Entity.contact]: [],
    [Entity.install]: [],
    [Entity.hotspotMaker]: []
  });
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );
  const showRelativePerformance = useMapConfig(
    (state) => state.showRelativePerformance
  );
  const setShowRelativePerformance = useMapConfig(
    (state) => state.setShowRelativePerformance
  );

  const smartLabelFilters: {
    [Entity.hotspot]: {
      [key: string]: (hotspot: Hotspot) => boolean;
    };
    [Entity.hex]: {
      [key: string]: (hex: HexTransmitScaleInfo) => boolean;
    };
    [Entity.location]: {
      [key: string]: (location: HotspotLocation) => boolean;
    };
    [Entity.contact]: {
      [key: string]: (contact: Contact) => boolean;
    };
    [Entity.install]: {
      [key: string]: (install: Install) => boolean;
    };
    [Entity.hotspotMaker]: {
      [key: string]: (maker: HotspotMaker) => boolean;
    };
  } = useMemo(
    () => ({
      [Entity.hotspot]: {
        verified: ({ owner }) => !!walletVerificationStatusMap[owner],
        "location asserted in past 24h": ({ last_assertion }) =>
          !!last_assertion &&
          moment()
            .utc()
            .subtract(24, "hours")
            .isBefore(moment.unix(last_assertion).utc()),
        "location asserted in past 7d": ({ last_assertion }) =>
          !!last_assertion &&
          moment()
            .utc()
            .subtract(7, "days")
            .isBefore(moment.unix(last_assertion).utc()),
        active: ({ online }) => online,
        inactive: ({ online }) => !online,
        activated: ({ id }) => isTransmitScaleActive(id),
        denylist: ({ deny_list }) => deny_list,
        ...reduce(
          [
            "excellent",
            "good",
            "mediocre",
            "poor",
            "terrible",
            "inactive",
            "disabled"
          ],
          (
            acc: {
              [key: string]: (hotspot: Hotspot) => boolean;
            },
            classification
          ) => {
            acc[`${classification} transmit scale`] = ({ id }) => {
              const isActive = isTransmitScaleActive(id);
              const locationClassification = isActive
                ? getClassification(get(calculatedTransmitScales, id, 0))
                : "disabled";
              return classification === locationClassification;
            };

            return acc;
          },
          {}
        )
      },
      [Entity.hex]: {
        "my discord channels": ({ h3Index }) =>
          !!user?.uuid && channels[h3Index]?.created_by === user.uuid,
        "with discord channel": ({ h3Index }) => !!channels[h3Index],
        ...reduce(
          range(4, isHip17Experiment ? 10 : 11),
          (
            acc: {
              [key: string]: (hex: HexTransmitScaleInfo) => boolean;
            },
            resolution
          ) => {
            acc[`resolution ${resolution}`] = (hex) =>
              hex.resolution === resolution;

            return acc;
          },
          {}
        ),
        ...reduce(
          [
            "excellent",
            "good",
            "mediocre",
            "poor",
            "terrible",
            "inactive",
            "disabled"
          ],
          (
            acc: {
              [key: string]: (hex: HexTransmitScaleInfo) => boolean;
            },
            classification
          ) => {
            acc[`${classification} transmit scale`] = ({ transmitScale }) =>
              classification === getClassification(transmitScale);

            return acc;
          },
          {}
        )
      },
      [Entity.location]: {
        hosting: ({ uuid }) => {
          const linkedActiveInstalls = filter(
            values(installs).filter(isActiveInstall),
            { location_uuid: uuid }
          );

          return !isEmpty(linkedActiveInstalls);
        },
        activated: ({ uuid }) => isTransmitScaleActive(uuid),
        ...reduce(
          [
            "excellent",
            "good",
            "mediocre",
            "poor",
            "terrible",
            "inactive",
            "disabled"
          ],
          (
            acc: {
              [key: string]: (location: HotspotLocation) => boolean;
            },
            classification
          ) => {
            acc[`${classification} transmit scale`] = ({ uuid }) => {
              const isActive = isTransmitScaleActive(uuid);
              const locationClassification = isActive
                ? getClassification(get(calculatedTransmitScales, uuid, 0))
                : "disabled";
              return classification === locationClassification;
            };

            return acc;
          },
          {}
        )
      },
      [Entity.contact]: {
        hosting: ({ linked_location_ids }) => {
          const linkedActiveInstalls = values(installs)
            .filter(isActiveInstall)
            .filter(({ location_uuid }) =>
              linked_location_ids.includes(location_uuid)
            );

          return !isEmpty(linkedActiveInstalls);
        }
      },
      [Entity.install]: {
        "active install": (install) => isActiveInstall(install),
        "future install": ({ installed_at }) => !installed_at,
        "past install": (install) =>
          !!install.installed_at && !isActiveInstall(install)
      },
      [Entity.hotspotMaker]: {
        "new makers": (hotspotMaker) =>
          !hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ],
        IOT: (hotspotMaker) =>
          hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ]?.labels.includes("IOT"),
        MOBILE: (hotspotMaker) =>
          hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ]?.labels.includes("MOBILE"),
        BANNED: (hotspotMaker) =>
          hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ]?.labels.includes("BANNED"),
        "Remote Diagnostics": (hotspotMaker) =>
          hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ]?.labels.includes("Remote Diagnostics"),
        "Hotspotty Partner": (hotspotMaker) =>
          hotspotMakerMetadata[
            hotspotMaker.maker_id as HotspotMakerAddressKey
          ]?.labels.includes("Hotspotty Partner")
      }
    }),
    [
      isHip17Experiment,
      calculatedTransmitScales,
      channels,
      isTransmitScaleActive,
      installs,
      user.uuid,
      walletVerificationStatusMap
    ]
  );

  // Generate filtered hotspots
  const filteredHotspotLabels = useMemo(
    () =>
      filter(
        getFilteredLabels(
          filteredLabels[LabelFilterKey.mapHotspots],
          labels,
          smartLabelFilters[Entity.hotspot],
          Entity.hotspot
        ),
        {
          type: Entity.hotspot
        }
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredWalletLabels = useMemo(
    () =>
      filter(
        getFilteredLabels(
          filteredLabels[LabelFilterKey.mapHotspots],
          labels,
          smartLabelFilters[Entity.hotspot],
          Entity.hotspot
        ),
        {
          type: Entity.wallet
        }
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredWalletUuids = useMemo(() => {
    const data: string[] = [];

    forEach(linkedLabels, (linkedLabelUuids, walletUuid) => {
      filteredWalletLabels.forEach(({ uuid }) => {
        if (linkedLabelUuids.includes(uuid)) {
          data.push(walletUuid);
        }
      });
    });

    return data;
  }, [filteredWalletLabels, linkedLabels]);

  const filteredHotspotsToList = useMemo(() => {
    const filteredWalletAddresses = compact(
      filteredWalletUuids.map((walletUuid) => wallets[walletUuid]?.address)
    );

    return hotspotsAroundCenter.filter((hotspot) => {
      if (
        !isEmpty(filteredHotspotLabels) &&
        !every(filteredHotspotLabels, ({ uuid }) => {
          const smartFilterFunction = (
            smartLabelFilters[Entity.hotspot] as any
          )[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(hotspot)
            : (linkedLabels[hotspot.id] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (
        !isEmpty(filteredWalletAddresses) &&
        !filteredWalletAddresses.includes(hotspot.owner)
      ) {
        return false;
      }

      return true;
    });
  }, [
    filteredHotspotLabels,
    filteredWalletUuids,
    hotspotsAroundCenter,
    linkedLabels,
    smartLabelFilters,
    wallets
  ]);

  useEffect(() => {
    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.hotspot]: sortBy(
        filteredHotspotsToList,
        (hotspot) => -calculatedTransmitScales[hotspot.id]
      )
    }));
  }, [
    calculatedTransmitScales,
    filteredHotspotLabels,
    filteredHotspotsToList,
    filteredLabels,
    filteredWalletLabels,
    hotspotsAroundCenter,
    labels,
    linkedLabels,
    smartLabelFilters,
    wallets
  ]);

  // Generate filtered hexes
  useEffect(() => {
    const filteredHexLabels = filter(
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapHexes],
        labels,
        smartLabelFilters[Entity.hex],
        Entity.hex
      ),
      {
        type: Entity.hex
      }
    );

    const hexChannels = filter(values(channels), { type: Entity.hex });
    const allHexes = uniq(
      map(hexChannels, "linked_id").concat(problematicHexes)
    );

    const filteredHexes = allHexes
      .map((h3Index) => getHexTransmitScaleInfo(h3Index))
      .filter((hex) => {
        if (
          !isEmpty(filteredHexLabels) &&
          !every(filteredHexLabels, ({ uuid }) => {
            const smartFilterFunction = (smartLabelFilters[Entity.hex] as any)[
              uuid
            ]; // TODO: fix typing

            return smartFilterFunction ? smartFilterFunction(hex) : false;
          })
        ) {
          return false;
        }

        return true;
      });

    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.hex]: orderBy(filteredHexes, "transmitScale", "desc")
    }));
  }, [
    channels,
    filteredLabels,
    getHexTransmitScaleInfo,
    labels,
    problematicHexes,
    smartLabelFilters
  ]);

  // Generate filtered locations
  useEffect(() => {
    const filteredLocationLabels = filter(
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapLocations],
        labels,
        smartLabelFilters[Entity.location],
        Entity.location
      ),
      {
        type: Entity.location
      }
    );

    const filteredLocations = locationsAroundCenter.filter((location) => {
      if (
        !isEmpty(filteredLocationLabels) &&
        !every(filteredLocationLabels, ({ uuid }) => {
          const smartFilterFunction = (
            smartLabelFilters[Entity.location] as any
          )[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(location)
            : (linkedLabels[location.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      return true;
    });

    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.location]: sortBy(
        filteredLocations,
        (location) => -calculatedTransmitScales[location.uuid]
      )
    }));
  }, [
    calculatedTransmitScales,
    filteredLabels,
    locationsAroundCenter,
    linkedLabels,
    labels,
    smartLabelFilters
  ]);

  // Generate filtered contacts
  useEffect(() => {
    const filteredContactLabels = filter(
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapContacts],
        labels,
        smartLabelFilters[Entity.contact],
        Entity.contact
      ),
      {
        type: Entity.contact
      }
    );

    const filteredContacts = values(contacts).filter((contact) => {
      if (
        !isEmpty(filteredContactLabels) &&
        !every(filteredContactLabels, ({ uuid }) => {
          const smartFilterFunction = (
            smartLabelFilters[Entity.contact] as any
          )[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(contact)
            : (linkedLabels[contact.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      return true;
    });

    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.contact]: orderBy(
        filteredContacts,
        [(contact) => toLower(contact.name)],
        ["asc"]
      )
    }));
  }, [
    calculatedTransmitScales,
    contacts,
    filteredLabels,
    labels,
    linkedLabels,
    smartLabelFilters
  ]);

  // Generate filtered installs
  useEffect(() => {
    const filteredInstallLabels = filter(
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapInstalls],
        labels,
        smartLabelFilters[Entity.install],
        Entity.install
      ),
      {
        type: Entity.install
      }
    );

    const filteredInstalls = values(installs).filter((install) => {
      if (
        !isEmpty(filteredInstallLabels) &&
        !every(filteredInstallLabels, ({ uuid }) => {
          const smartFilterFunction = (
            smartLabelFilters[Entity.install] as any
          )[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(install)
            : (linkedLabels[install.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      return true;
    });

    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.install]: orderBy(
        filteredInstalls,
        [(install) => toLower(install.installed_at)],
        ["asc"]
      )
    }));
  }, [
    contacts,
    filteredLabels,
    installs,
    labels,
    linkedLabels,
    smartLabelFilters
  ]);

  // Generate filtered makers
  useEffect(() => {
    const filteredMakersLabels = filter(
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapHotspotMakers],
        labels,
        smartLabelFilters[Entity.hotspotMaker],
        Entity.hotspotMaker
      ),
      {
        type: Entity.hotspotMaker
      }
    );

    const filteredMakers = values(hotspotMakersById).filter((maker) => {
      if (unlistedMakers.includes(maker.maker_id as HotspotMakerAddressKey)) {
        return false;
      }

      if (
        !isEmpty(filteredMakersLabels) &&
        !every(filteredMakersLabels, ({ uuid }) => {
          const smartFilterFunction = (
            smartLabelFilters[Entity.hotspotMaker] as any
          )[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(maker)
            : (linkedLabels[maker.maker_id] || []).includes(uuid);
        })
      ) {
        return false;
      }

      return true;
    });

    setFilteredList((prevFilteredList) => ({
      ...prevFilteredList,
      [Entity.hotspotMaker]: orderBy(
        filteredMakers,
        [
          (maker) =>
            filter(hotspotsAroundCenter, { payer: maker.maker_id }).length
        ],
        ["desc"]
      )
    }));
  }, [
    contacts,
    filteredLabels,
    hotspotMakersById,
    hotspotsAroundCenter,
    labels,
    linkedLabels,
    smartLabelFilters
  ]);

  const getClassificationValue = useCallback(
    (hotspot: Hotspot, key: string): number => {
      if (key === "reward_scale") {
        return isTransmitScaleActive(hotspot.id)
          ? get(calculatedTransmitScales, hotspot.id, 0)
          : 0;
      }

      const iterateeFields = key.split(".");
      if (iterateeFields.length > 1) {
        const [type, ...otherIterateeFields] = iterateeFields;

        switch (type) {
          case "rewardsSummary":
            return (
              get(hotspotRewardsSummary, [
                hotspot.id,
                ...otherIterateeFields
              ]) || 0
            );
          case "witnessesSummary":
            return (
              get(hotspotWitnessesCountsMap, [
                hotspot.id,
                ...otherIterateeFields
              ]) || 0
            );
        }
      }

      return get(hotspot, key, 0);
    },
    [
      calculatedTransmitScales,
      isTransmitScaleActive,
      hotspotRewardsSummary,
      hotspotWitnessesCountsMap
    ]
  );

  const renderClassificationValue = useCallback(
    (value: number, key: string) => {
      const iterateeFields = key.split(".");

      if (iterateeFields.length > 1) {
        const [type] = iterateeFields;

        switch (type) {
          case "rewardsSummary":
            return displayInCurrency(value, { needsDivision: true });
          case "witnessesSummary":
            return value.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
        }
      }

      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    [displayInCurrency]
  );

  const filteredHotspotAndLocationIds = useMemo(
    () =>
      map(filteredList[Entity.hotspot], "id")
        .concat(map(filteredList[Entity.location], "uuid"))
        .filter((id) => isTransmitScaleActive(id)),
    [filteredList, isTransmitScaleActive]
  );

  const relativePerformanceMetricsToCalculate = useMemo(
    () =>
      relativePerformanceMetrics.filter((metric) => {
        if (showRelativePerformance) return true;
        return metric.key === pointColorMetricIteratee;
      }),
    [pointColorMetricIteratee, showRelativePerformance]
  );

  const { histogramDataMap, histogramBucketIndexMap } = useMemo(() => {
    const histogramDataMap: {
      [iteratee: string]: Array<{ name: string; count: number }>;
    } = {};

    const histogramBucketIndexMap: {
      [hotspotId: string]: { [iteratee: string]: number };
    } = {};

    const filteredHotspots = filteredList[Entity.hotspot];

    if (isEmpty(filteredHotspots))
      return { histogramDataMap, histogramBucketIndexMap };

    relativePerformanceMetricsToCalculate.forEach(({ key, title }) => {
      let renderBoundaryValue: ((value: number) => string) | undefined;

      if (key.startsWith("rewardsSummary")) {
        renderBoundaryValue = (value: number) =>
          displayInCurrency(value, { needsDivision: true });
      } else if (key.startsWith("witnessesSummary")) {
        renderBoundaryValue = (value: number) =>
          value.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
      }

      const hotspotsAndClassifications: {
        hotspot: EtlHotspot;
        classificationValue: number;
      }[] = filteredHotspots.map((hotspot) => ({
        hotspot,
        classificationValue: getClassificationValue(hotspot, key)
      }));

      const [zeroValueHotspots, valueHotspots] = partition(
        hotspotsAndClassifications,
        ({ classificationValue }) => classificationValue === 0
      );

      const sortedHotspots = valueHotspots.sort(
        (a, b) => a.classificationValue - b.classificationValue
      );

      const sortedValues = sortedHotspots.map(
        ({ classificationValue }) => classificationValue
      );

      if (key === "status") {
        histogramDataMap[key] = relativePerformanceClassifications.reduce(
          (
            acc: Array<{ name: string; count: number }>,
            classification: string,
            binIndex: number
          ) => {
            let bucket = { name: "", count: 0 };

            if (classification === "excellent") {
              const count = filter(filteredHotspots, (hotspot) => {
                const isValueInBucket = !!hotspot.online;

                if (isValueInBucket) {
                  if (histogramBucketIndexMap[hotspot.id]) {
                    histogramBucketIndexMap[hotspot.id][key] = binIndex;
                  } else {
                    histogramBucketIndexMap[hotspot.id] = {
                      [key]: binIndex
                    };
                  }
                }

                return isValueInBucket;
              }).length;

              bucket = {
                name: `active hotspot${count === 1 ? "" : "s"}`,
                count
              };
            } else if (classification === "terrible") {
              const count = filter(filteredHotspots, (hotspot) => {
                const isValueInBucket = !hotspot.online;

                if (isValueInBucket) {
                  if (histogramBucketIndexMap[hotspot.id]) {
                    histogramBucketIndexMap[hotspot.id][key] = binIndex;
                  } else {
                    histogramBucketIndexMap[hotspot.id] = {
                      [key]: binIndex
                    };
                  }
                }

                return isValueInBucket;
              }).length;

              bucket = {
                name: `inactive hotspot${count === 1 ? "" : "s"}`,
                count
              };
            }

            return [...acc, bucket];
          },
          []
        );
      } else if (key === "verified") {
        histogramDataMap[key] = relativePerformanceClassifications.reduce(
          (
            acc: Array<{ name: string; count: number }>,
            classification: string,
            binIndex: number
          ) => {
            let bucket = { name: "", count: 0 };

            if (classification === "excellent") {
              const count = filter(filteredHotspots, (hotspot) => {
                const isValueInBucket =
                  !!walletVerificationStatusMap[hotspot.owner];

                if (isValueInBucket) {
                  if (histogramBucketIndexMap[hotspot.id]) {
                    histogramBucketIndexMap[hotspot.id][key] = binIndex;
                  } else {
                    histogramBucketIndexMap[hotspot.id] = {
                      [key]: binIndex
                    };
                  }
                }

                return isValueInBucket;
              }).length;

              bucket = {
                name: `verified hotspot${count === 1 ? "" : "s"}`,
                count
              };
            } else if (classification === "terrible") {
              const count = filter(filteredHotspots, (hotspot) => {
                const isValueInBucket =
                  !!walletVerificationStatusMap[hotspot.owner];

                if (isValueInBucket) {
                  if (histogramBucketIndexMap[hotspot.id]) {
                    histogramBucketIndexMap[hotspot.id][key] = binIndex;
                  } else {
                    histogramBucketIndexMap[hotspot.id] = {
                      [key]: binIndex
                    };
                  }
                }

                return isValueInBucket;
              }).length;

              bucket = {
                name: `unverified hotspot${count === 1 ? "" : "s"}`,
                count
              };
            }

            return [...acc, bucket];
          },
          []
        );
      } else if (key === "reward_scale") {
        histogramDataMap[key] = relativePerformanceClassifications.reduce(
          (
            acc: Array<{ name: string; count: number }>,
            classification: string,
            binIndex: number
          ) => {
            const count = filter(filteredHotspotAndLocationIds, (id) => {
              const isValueInBucket =
                getClassification(calculatedTransmitScales[id]) ===
                classification;

              if (isValueInBucket) {
                if (histogramBucketIndexMap[id]) {
                  histogramBucketIndexMap[id][key] = binIndex;
                } else {
                  histogramBucketIndexMap[id] = {
                    [key]: binIndex
                  };
                }
              }

              return isValueInBucket;
            }).length;

            return [
              ...acc,
              {
                name: `hotspot/location${
                  count === 1 ? "" : "s"
                } with a ${classification} ${title} (${
                  classificationBoundaries[classification][0]
                } to ${classificationBoundaries[classification][1]})`,
                count
              }
            ];
          },
          []
        );
      } else if (key === "makers") {
        histogramDataMap[key] = relativePerformanceClassifications.reduce(
          (acc: Array<{ name: string; count: number }>) => {
            let bucket = {
              name: "test",
              count: 20
            };

            return [...acc, bucket];
          },
          []
        );
      } else {
        histogramDataMap[key] = relativePerformanceClassifications.reduce(
          (
            acc: Array<{ name: string; count: number }>,
            classification: string,
            binIndex: number
          ) => {
            let count = 0;
            let bucketMinValue = 0;
            let bucketMaxValue = 0;

            if (classification === "inactive") {
              bucketMinValue = 0;
              bucketMaxValue = 0;
              count = zeroValueHotspots.length;
              zeroValueHotspots.forEach(({ hotspot }) => {
                if (histogramBucketIndexMap[hotspot.id]) {
                  histogramBucketIndexMap[hotspot.id][key] = binIndex;
                } else {
                  histogramBucketIndexMap[hotspot.id] = {
                    [key]: binIndex
                  };
                }
              });
            } else {
              const [minPercentile, maxPercentile] =
                classificationBoundaries[classification];

              const bucketMinIndex = Math.floor(
                minPercentile * sortedValues.length
              );
              const bucketMaxIndex = Math.floor(
                maxPercentile * sortedValues.length
              );

              bucketMinValue = sortedValues[bucketMinIndex];
              bucketMaxValue = sortedValues[bucketMaxIndex];

              bucketMaxValue =
                binIndex === keys(classificationBoundaries).length - 1
                  ? (last(sortedValues) as number)
                  : sortedValues[bucketMaxIndex];

              count = filter(sortedHotspots, ({ hotspot }, index) => {
                const isValueInBucket =
                  index > bucketMinIndex && index <= bucketMaxIndex;

                if (isValueInBucket) {
                  if (histogramBucketIndexMap[hotspot.id]) {
                    histogramBucketIndexMap[hotspot.id][key] = binIndex;
                  } else {
                    histogramBucketIndexMap[hotspot.id] = {
                      [key]: binIndex
                    };
                  }
                }

                return isValueInBucket;
              }).length;
            }

            let name = `hotspot${
              count === 1 ? "" : "s"
            } with ${classification} ${title}`;

            if (
              renderBoundaryValue &&
              bucketMinValue != null &&
              bucketMaxValue != null
            ) {
              name += ` of ${renderBoundaryValue(
                bucketMinValue
              )} to ${renderBoundaryValue(bucketMaxValue)}`;
            }

            return [...acc, { name, count }];
          },
          []
        );
      }
    });

    return { histogramDataMap, histogramBucketIndexMap };
  }, [
    filteredList,
    relativePerformanceMetricsToCalculate,
    displayInCurrency,
    getClassificationValue,
    walletVerificationStatusMap,
    filteredHotspotAndLocationIds,
    calculatedTransmitScales
  ]);

  const toggleShowRelativePerformance = useCallback(
    (entity: Entity, metricIteratee: string) => {
      trackEvent("toggle relative performance", {
        event_category: "user settings",
        event_label: `${entity} - ${metricIteratee}`,
        value: !showRelativePerformance ? 1 : 0
      });
      setShowRelativePerformance(!showRelativePerformance);
    },
    [setShowRelativePerformance, showRelativePerformance, trackEvent]
  );

  const value = useMemo(
    () => ({
      filteredList,
      getClassificationValue,
      renderClassificationValue,
      histogramDataMap,
      histogramBucketIndexMap,
      showRelativePerformance,
      toggleShowRelativePerformance,
      smartLabelFilters
    }),
    [
      filteredList,
      getClassificationValue,
      histogramBucketIndexMap,
      histogramDataMap,
      renderClassificationValue,
      showRelativePerformance,
      smartLabelFilters,
      toggleShowRelativePerformance
    ]
  );

  return <FilteredListContext.Provider value={value} {...props} />;
};

export const useFilteredList = (): ContextValue => {
  const context = useContext(FilteredListContext);
  if (context === undefined) {
    throw new Error(
      "useFilteredList must be used within an FilteredListProvider"
    );
  }
  return context;
};

//
// Utils
//

export const getSmartLabelColor = (key: string) => {
  switch (key) {
    case "failed":
      return "#B60205";
    case "paid":
      return "#0E8A16";
    case "unpaid":
      return "#a2eeef";
    default:
      return getColorFromId(key);
  }
};

export const getSmartLabelFromKey = (key: string, entity: Entity) => ({
  uuid: key,
  workspaceUuid: "",
  type: entity,
  name: key,
  color: getSmartLabelColor(key),
  order: 0
});

export const isHistogramDataLoading = (
  histogramDataMap: {
    [iteratee: string]: Array<{ name: string; count: number }>;
  },
  key: string
) => {
  const histogramData = histogramDataMap[key];

  if (!histogramData) return true;

  const [first, ...others] = histogramData;

  return first.count > 0 && sumBy(others, "count") === 0;
};
